<template>
  <div class="tc-grid-panel" data-testid="timesheet-grid-panel">
    <!-- Always mounted so screen readers announce the text change. -->
    <span class="visually-hidden" role="status" data-testid="draft-loading-status">{{ draftLoading ? 'Loading draft' : '' }}</span>
    <div class="tc-grid-scroll tc-scroll">
      <table class="tc-grid" role="grid" aria-label="Timesheet grid" :aria-busy="draftLoading">
        <thead>
          <tr>
            <th
              v-for="(g, ci) in geometry"
              :key="g.col.key"
              scope="col"
              class="tc-grid-left"
              :class="{ 'tc-grid-left--last': ci === geometry.length - 1 }"
              :style="leftStyle(g)"
              :aria-sort="ariaSort(g.col.key)"
              :data-testid="`preview-col-${g.col.key}`"
            >
              <button
                type="button"
                class="tc-grid-sort"
                :title="`Sort by ${g.col.label}`"
                :data-testid="`grid-sort-${g.col.key}`"
                @click="cycle(g.col.key)"
              >
                <span class="tc-grid-sort-label">{{ g.col.label }}</span>
                <span class="tc-grid-sort-ind" aria-hidden="true">{{ indicator(g.col.key) }}</span>
              </button>
              <span
                class="tc-grid-resize"
                :class="{ 'tc-grid-resize--active': drag?.key === g.col.key }"
                aria-hidden="true"
                title="Drag to resize, double-click to reset"
                :data-testid="`grid-resize-${g.col.key}`"
                @pointerdown="startResize(g.col, $event)"
                @dblclick="columns.resetWidth(g.col.key)"
              ></span>
            </th>
            <th
              v-for="(day, di) in periodDays"
              :key="di"
              scope="col"
              class="tc-grid-day-head"
              :class="{ 'tc-grid-nonworking': day.isNonWorking }"
              data-testid="preview-col-day"
            >
              <span class="tc-grid-day-dow">{{ day.dow }}</span>
              <span class="tc-grid-day-date">{{ day.month }}/{{ day.day }}</span>
            </th>
            <th scope="col" class="tc-grid-total tc-grid-right-total" :aria-sort="ariaSort('total')">
              <button
                type="button"
                class="tc-grid-sort tc-grid-sort--end"
                title="Sort by Total"
                data-testid="grid-sort-total"
                @click="cycle('total')"
              >
                <span class="tc-grid-sort-label">Total</span>
                <span class="tc-grid-sort-ind" aria-hidden="true">{{ indicator('total') }}</span>
              </button>
            </th>
            <th scope="col" class="tc-grid-act tc-grid-right-act">
              <button
                ref="gearRef"
                type="button"
                class="tc-btn-icon tc-grid-gear"
                aria-haspopup="dialog"
                :aria-expanded="menuOpen"
                aria-label="Column settings"
                title="Column settings"
                data-testid="grid-gear"
                @click="menuOpen = !menuOpen"
              >
                <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M8 10.2a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4Z" stroke="currentColor" stroke-width="1.3"/>
                  <path d="M13.1 9.6l1-.6-.1-2-1-.4-.5-1.2.4-1-1.4-1.4-1 .4-1.2-.5L9 1.9H7l-.4 1-1.2.5-1-.4L3 4.4l.4 1-.5 1.2-1 .4v2l1 .4.5 1.2-.4 1L4.4 13l1-.4 1.2.5.4 1h2l.4-1 1.2-.5 1 .4 1.4-1.4-.4-1 .5-1.2Z" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/>
                </svg>
              </button>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="rows.length === 0 && !draftLoading">
            <td :colspan="span" class="tc-grid-empty" data-testid="preview-empty-state">No entries yet.</td>
          </tr>

          <template v-for="group in groups" :key="group.id">
            <tr class="tc-grid-band" :data-testid="`preview-group-${group.id}`">
              <td :colspan="span"><span class="tc-grid-band-label">{{ group.label }}</span></td>
            </tr>
            <tr
              v-for="row in group.rows"
              :key="row.rowKey"
              :data-row-wbs1="row.wbs1"
              class="tc-grid-row"
              :class="[
                row.isGhost ? 'tc-grid-row--ghost' : (row.isDraft ? 'tc-grid-row--draft' : 'tc-grid-row--vp'),
                isHighlighted(row) ? 'tc-preview-row--highlighted' : '',
              ]"
              :data-testid="row.isDraft ? 'preview-draft-row' : 'preview-existing-row'"
              @click="row.isDraft && emit('rowClick', row.rowKey)"
            >
              <td
                v-for="(g, ci) in geometry"
                :key="g.col.key"
                class="tc-grid-left"
                :class="[
                  ci === geometry.length - 1 ? 'tc-grid-left--last' : '',
                  g.col.code ? 'tc-grid-code' : '',
                  leftCellActionable(row, g.col.key) ? 'tc-grid-cell--action' : '',
                  promptAt(row) === g.col.key ? 'tc-grid-cell--prompt' : '',
                ]"
                :style="leftStyle(g)"
                :title="cellText(row, g.col.key) || undefined"
                :data-testid="leftCellTestId(row, g.col.key)"
                @click="onLeftCellClick(row, g.col.key, $event)"
              >
                <ConfidenceBar
                  v-if="ci === 0 && row.isDraft && row.confBar"
                  :state="row.confBar"
                  variant="bar"
                  class="tc-grid-edge"
                  :data-testid="`preview-conf-bar-${row.confBar}`"
                />
                <button
                  v-if="leftCellActionable(row, g.col.key)"
                  type="button"
                  class="tc-btn-cell"
                  :aria-label="leftCellAriaLabel(row, g.col.key)"
                  @click.stop="onLeftCellClick(row, g.col.key, $event)"
                >{{ cellText(row, g.col.key) }}</button>
                <span v-else class="tc-grid-text">{{ cellText(row, g.col.key) }}</span>
                <span
                  v-if="g.col.key === 'wbs1' && regenerating.has(row.rowKey)"
                  class="spinner-border spinner-border-sm ms-1"
                  role="status"
                  aria-label="Generating note…"
                  data-testid="row-note-regenerating"
                ></span>
                <button
                  v-if="promptAt(row) === g.col.key"
                  type="button"
                  class="tc-btn-needs tc-grid-need"
                  :data-testid="row.needsPhase ? 'preview-phase-select-btn' : 'preview-task-select-btn'"
                  :aria-label="`${row.wbs1} — select ${(row.needsPhase ? wbs2Label : wbs3Label).toLowerCase()}`"
                  @click.stop="emit('phaseCellClick', row.rowKey, $event.currentTarget as HTMLElement, row.needsPhase ? 'phase' : 'task')"
                >{{ row.needsPhase ? `Select ${wbs2Label.toLowerCase()}` : `Select ${wbs3Label.toLowerCase()}` }} ▾</button>
              </td>

              <td
                v-for="(day, di) in periodDays"
                :key="di"
                class="tc-preview-day-cell tc-grid-day"
                :class="[
                  day.isNonWorking ? 'tc-grid-nonworking' : '',
                  (row.days[di] || row.ghostDay === di) ? 'tc-preview-day-cell--value' : 'tc-grid-day--empty',
                  row.uncertain[di] ? 'tc-preview-day-cell--uncertain' : '',
                  row.isDraft ? 'tc-preview-day-cell--editable' : '',
                  row.ghostDay === di ? 'tc-preview-day-cell--ghost' : '',
                  isCellHighlighted(row, di) ? 'tc-preview-day-cell--selected' : '',
                  row.comments[di] ? 'tc-grid-day--comment' : '',
                ]"
                :title="row.comments[di] ? `Comment: ${row.comments[di]}` : undefined"
                :aria-label="cellAriaLabel(row, di, day.dow)"
                @click="onDayCellClick(row, di, $event)"
              >{{ ghostCellText(row, di) ?? (row.days[di] ? row.days[di]!.toFixed(2) : '') }}</td>

              <td class="tc-grid-total tc-grid-right-total">{{ rowTotal(row).toFixed(2) }}</td>
              <td class="tc-grid-act tc-grid-right-act">
                <button
                  v-if="row.isDraft && !row.isGhost"
                  type="button"
                  class="tc-preview-row-remove"
                  :aria-label="`Remove ${row.wbs1} from the draft`"
                  :title="`Remove ${row.wbs1} from the draft`"
                  :data-testid="`preview-row-remove-${row.rowKey}`"
                  @click.stop="emit('rowRemove', row.rowKey)"
                >
                  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M3 5h10M6.5 5V3.5h3V5M5 5l.6 8h4.8L11 5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </td>
            </tr>
          </template>

          <!-- The draft is still on its way: VP rows above are real, these hold its place. -->
          <template v-if="draftLoading">
            <tr class="tc-grid-band" aria-hidden="true">
              <td :colspan="span"><span class="tc-grid-band-label">Your Draft</span></td>
            </tr>
            <tr
              v-for="n in 3"
              :key="`draft-placeholder-${n}`"
              class="placeholder-glow"
              aria-hidden="true"
              data-testid="draft-placeholder-row"
            >
              <td :colspan="span" class="tc-grid-placeholder">
                <span class="placeholder col-12 rounded"></span>
              </td>
            </tr>
          </template>
        </tbody>

        <tfoot>
          <template v-if="hasOvertime">
            <tr class="tc-grid-foot" data-testid="grid-footer-regular">
              <td class="tc-grid-foot-label" :colspan="geometry.length" :style="footLabelStyle">Regular</td>
              <td
                v-for="(day, di) in periodDays"
                :key="di"
                class="tc-grid-day"
                :class="{ 'tc-grid-nonworking': day.isNonWorking }"
              >{{ regularTotals[di] ? regularTotals[di].toFixed(2) : '' }}</td>
              <td class="tc-grid-total tc-grid-right-total">{{ sum(regularTotals).toFixed(2) }}</td>
              <td class="tc-grid-act tc-grid-right-act"></td>
            </tr>
            <tr class="tc-grid-foot" data-testid="grid-footer-overtime">
              <td class="tc-grid-foot-label" :colspan="geometry.length" :style="footLabelStyle">Overtime</td>
              <td
                v-for="(day, di) in periodDays"
                :key="di"
                class="tc-grid-day"
                :class="{ 'tc-grid-nonworking': day.isNonWorking }"
              >{{ overtimeTotals[di] ? overtimeTotals[di].toFixed(2) : '' }}</td>
              <td class="tc-grid-total tc-grid-right-total">{{ sum(overtimeTotals).toFixed(2) }}</td>
              <td class="tc-grid-act tc-grid-right-act"></td>
            </tr>
          </template>
          <tr class="tc-grid-foot tc-grid-foot--grand">
            <td class="tc-grid-foot-label" :colspan="geometry.length" :style="footLabelStyle">Total</td>
            <td
              v-for="(day, di) in periodDays"
              :key="di"
              class="tc-grid-day"
              :class="{ 'tc-grid-nonworking': day.isNonWorking }"
            >{{ dailyTotals[di] ? dailyTotals[di].toFixed(2) : '' }}</td>
            <td class="tc-grid-total tc-grid-right-total" data-testid="preview-footer-total">{{ sum(dailyTotals).toFixed(2) }}</td>
            <td class="tc-grid-act tc-grid-right-act"></td>
          </tr>
        </tfoot>
      </table>
    </div>

    <GridColumnMenu
      v-if="menuOpen"
      :columns="columns.menuColumns.value"
      :narrow="columns.narrow.value"
      :anchor="gearRef"
      @toggle="onToggleColumn"
      @reset="onResetColumns"
      @close="closeMenu"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import type { TimesheetRow } from '../api/client';
import type { BillingTarget, DraftResponse } from '../api/draft';
import { periodDayCount } from '../utils/period';
import ConfidenceBar from './ui/ConfidenceBar.vue';
import GridColumnMenu from './GridColumnMenu.vue';
import {
  buildPeriodDays,
  buildPreviewRows,
  cellAriaLabel,
  ghostCellText,
  isCellHighlighted as isCellHighlightedFor,
  isRowHighlighted,
  regeneratingRowKeys,
  rowTotal,
  taskCellActionable,
  type HighlightCell,
  type PreviewRow,
} from './timesheetGridRows';
import { useGridColumns, type GridColumn, type GridColumnKey } from '../composables/useGridColumns';
import { useGridSort, type GridSortKey } from '../composables/useGridSort';

const props = withDefaults(defineProps<{
  vpRows: TimesheetRow[];
  periodStart: string;
  periodEnd: string;
  draftRows?: DraftResponse;
  // The draft request is in flight: placeholder rows stand in for the draft block.
  draftLoading?: boolean;
  highlightWbs?: string;
  highlightScope?: 'draft' | 'vp';
  highlightCell?: HighlightCell | null;
  regeneratingEventIds?: Set<string>;
  wbs1Label?: string;
  wbs2Label?: string;
  wbs3Label?: string;
  // VP's own column switches. A column the tenant turns off is not shown and
  // not offered in the gear menu.
  showWbs2?: boolean;
  showWbs3?: boolean;
  showFirmName?: boolean;
  ghostCell?: { target: BillingTarget; dayIndex: number; hours: number } | null;
  nonWorkingDays?: number[];
}>(), {
  wbs1Label: 'Project',
  wbs2Label: 'Phase',
  wbs3Label: 'Task',
  showWbs2: true,
  showWbs3: true,
  showFirmName: true,
  nonWorkingDays: () => [0, 6],
});

const emit = defineEmits<{
  rowClick: [rowKey: string];
  projectCellClick: [rowKey: string, anchor: HTMLElement];
  cellBadgeClick: [rowKey: string, dayIndex: number, anchor: HTMLElement];
  phaseCellClick: [rowKey: string, anchor: HTMLElement, field: 'phase' | 'task'];
  rowRemove: [rowKey: string];
}>();

const columns = useGridColumns({
  labels: () => ({ wbs1: props.wbs1Label, wbs2: props.wbs2Label, wbs3: props.wbs3Label }),
  tenant: () => ({ showWbs2: props.showWbs2, showWbs3: props.showWbs3, showFirmName: props.showFirmName }),
});
const { cycle, ariaSort, sortGroup, sortKey, sortDir, clear: clearSort } = useGridSort();

// A sorted column the tenant or the user just hid takes its sort with it.
watch(() => columns.visibleColumns.value.map(c => c.key), (keys) => {
  if (sortKey.value && sortKey.value !== 'total' && !keys.includes(sortKey.value)) clearSort();
});

function indicator(key: GridSortKey): string {
  if (sortKey.value !== key) return '';
  return sortDir.value === 'asc' ? '▲' : '▼';
}

// ---- Column geometry and resize ----

const drag = ref<{ key: GridColumnKey; width: number } | null>(null);

interface ColumnGeometry { col: GridColumn; width: number; left: number }

const geometry = computed((): ColumnGeometry[] => {
  let left = 0;
  return columns.visibleColumns.value.map(col => {
    const width = drag.value?.key === col.key ? drag.value.width : col.width;
    const g = { col, width, left };
    left += width;
    return g;
  });
});

const leftWidth = computed(() => geometry.value.reduce((s, g) => s + g.width, 0));

function leftStyle(g: ColumnGeometry): Record<string, string> {
  const w = `${g.width}px`;
  return { width: w, minWidth: w, maxWidth: w, left: `${g.left}px` };
}

const footLabelStyle = computed(() => {
  const w = `${leftWidth.value}px`;
  return { width: w, minWidth: w, maxWidth: w };
});

let stopDrag: (() => void) | null = null;

function startResize(col: GridColumn, e: PointerEvent): void {
  e.preventDefault();
  e.stopPropagation();
  stopDrag?.();
  const startX = e.clientX;
  const startW = col.width;
  drag.value = { key: col.key, width: startW };
  const move = (ev: PointerEvent) => {
    drag.value = { key: col.key, width: Math.max(col.minWidth, Math.round(startW + ev.clientX - startX)) };
  };
  const up = () => {
    const final = drag.value;
    stopDrag?.();
    if (final) columns.setWidth(final.key, final.width);
  };
  stopDrag = () => {
    document.removeEventListener('pointermove', move);
    document.removeEventListener('pointerup', up);
    document.removeEventListener('pointercancel', cancel);
    drag.value = null;
    stopDrag = null;
  };
  // A cancelled touch drag keeps the width the column had before it.
  const cancel = () => stopDrag?.();
  document.addEventListener('pointermove', move);
  document.addEventListener('pointerup', up);
  document.addEventListener('pointercancel', cancel);
}

onBeforeUnmount(() => stopDrag?.());

// ---- Gear menu ----

const menuOpen = ref(false);
const gearRef = ref<HTMLButtonElement | null>(null);

function closeMenu(): void {
  menuOpen.value = false;
  gearRef.value?.focus();
}

function onToggleColumn(key: GridColumnKey, hidden: boolean): void {
  columns.setHidden(key, hidden);
}

function onResetColumns(): void {
  columns.reset();
}

// ---- Rows ----

const periodDays = computed(() =>
  buildPeriodDays(props.periodStart, periodDayCount(props.periodStart, props.periodEnd), props.nonWorkingDays),
);

const rows = computed((): PreviewRow[] => buildPreviewRows({
  vpRows: props.vpRows,
  draftRows: props.draftRows,
  ghostCell: props.ghostCell,
}));

const regenerating = computed(() => regeneratingRowKeys(props.draftRows, props.regeneratingEventIds));

const groups = computed(() => {
  const existing = rows.value.filter(r => !r.isDraft && !r.isGhost);
  const draft = rows.value.filter(r => r.isDraft || r.isGhost);
  const out: { id: string; label: string; rows: PreviewRow[] }[] = [];
  if (existing.length > 0) {
    const hours = existing.reduce((s, r) => s + rowTotal(r), 0);
    out.push({ id: 'invp', label: `In Vantagepoint · ${hours.toFixed(2)}h`, rows: sortGroup(existing) });
  }
  if (draft.length > 0) {
    const hours = draft.filter(r => !r.isGhost).reduce((s, r) => s + rowTotal(r), 0);
    out.push({ id: 'draft', label: `Your Draft · ${hours.toFixed(2)}h`, rows: sortGroup(draft) });
  }
  return out;
});

// Full-width cells: the left block, every day, Total and the row-action column.
const span = computed(() => geometry.value.length + periodDays.value.length + 2);

function cellText(row: PreviewRow, key: GridColumnKey): string {
  return row[key] ?? '';
}

// The missing phase or task must stay in view: when its own column is hidden,
// the prompt moves to the nearest visible code column to its left.
function promptAt(row: PreviewRow): GridColumnKey | null {
  if (!row.isDraft || (!row.needsPhase && !row.needsTask)) return null;
  const visible = new Set(geometry.value.map(g => g.col.key));
  const order: GridColumnKey[] = row.needsPhase ? ['wbs2', 'wbs1'] : ['wbs3', 'wbs2', 'wbs1'];
  return order.find(k => visible.has(k)) ?? 'wbs1';
}

type LeftAction = 'project' | 'phase' | 'task' | null;

function leftCellAction(row: PreviewRow, key: GridColumnKey): LeftAction {
  if (!row.isDraft) return null;
  if (key === 'wbs1' || key === 'wbs1Name') return 'project';
  if ((key === 'wbs2' || key === 'wbs2Name') && row.isPhased && !row.needsPhase) return 'phase';
  if ((key === 'wbs3' || key === 'wbs3Name') && taskCellActionable(row) && !row.needsTask) return 'task';
  return null;
}

function leftCellActionable(row: PreviewRow, key: GridColumnKey): boolean {
  return leftCellAction(row, key) !== null;
}

// Cell click handlers stop propagation only when the cell is actionable, so
// clicks on inert cells still bubble to the row's highlight toggle.
function onLeftCellClick(row: PreviewRow, key: GridColumnKey, e: MouseEvent): void {
  const action = leftCellAction(row, key);
  if (!action) return;
  e.stopPropagation();
  const anchor = ((e.currentTarget as HTMLElement).closest('td') ?? e.currentTarget) as HTMLElement;
  if (action === 'project') emit('projectCellClick', row.rowKey, anchor);
  else emit('phaseCellClick', row.rowKey, anchor, action);
}

function leftCellTestId(row: PreviewRow, key: GridColumnKey): string | undefined {
  if (key === 'wbs1') return 'preview-project-cell';
  if (!row.isDraft) return undefined;
  if (key === 'wbs2') return 'preview-phase-cell';
  if (key === 'wbs3') return 'preview-task-cell';
  return undefined;
}

function leftCellAriaLabel(row: PreviewRow, key: GridColumnKey): string | undefined {
  switch (leftCellAction(row, key)) {
    case 'project': return `${row.wbs1} — change ${props.wbs1Label.toLowerCase()}`;
    case 'phase': return `${row.wbs1} ${props.wbs2Label.toLowerCase()} ${row.wbs2} — change`;
    case 'task': return `${row.wbs1} ${props.wbs3Label.toLowerCase()} ${row.wbs3} — change`;
    default: return undefined;
  }
}

function onDayCellClick(row: PreviewRow, di: number, e: MouseEvent): void {
  if (!row.isDraft) return;
  e.stopPropagation();
  emit('cellBadgeClick', row.rowKey, di, e.currentTarget as HTMLElement);
}

function isHighlighted(row: PreviewRow): boolean {
  return isRowHighlighted(row, props.highlightWbs, props.highlightScope, props.highlightCell);
}

function isCellHighlighted(row: PreviewRow, dayIndex: number): boolean {
  return isCellHighlightedFor(row, dayIndex, props.highlightCell);
}

// ---- Totals ----

const sum = (xs: number[]) => xs.reduce((s, x) => s + x, 0);

const dailyTotals = computed(() =>
  periodDays.value.map((_, di) => rows.value.reduce((s, r) => s + (r.days[di] ?? 0), 0)),
);

const overtimeTotals = computed(() =>
  periodDays.value.map((_, di) => rows.value.reduce((s, r) => s + (r.overtime[di] ?? 0), 0)),
);

// Rounded, so a subtraction residue such as 5e-17 reads as an empty cell.
const regularTotals = computed(() =>
  dailyTotals.value.map((t, di) => Math.round((t - overtimeTotals.value[di]) * 100) / 100),
);

// Regular and Overtime lines only earn their space when the sheet has overtime.
const hasOvertime = computed(() => overtimeTotals.value.some(x => x > 0));
</script>
